import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { execMeetingAPI } from '../../api/client';
import { confirmDialog } from '../../components/AppDialog';
import { COLORS, RADIUS, badge, btn, card, input as inputStyle } from '../../utils/uiStyles';
import RichEditor from './RichEditor';
import { RICH_FIELDS } from './execMeetingConst';

// 임원회의 관리자 탭 «PDF 가져오기»(#3130 2번) — AdminTab 이 'PDF 가져오기' 칸을 펼칠 때만 붙인다.
//   ① PDF 고르기(.pdf · 20MB) → «읽기» → 서버가 백그라운드로 글·내장 그림을 읽는다(진행률)
//   ② 미리보기: 주차·부서 카드 — 부서·작성자·날짜·제목·라벨·본문(RichEditor)·KPI 를 고치고 카드마다 넣기/건너뛰기
//      그 주차에 같은 부서가 이미 있으면 «이미 있음 — 건너뜀»(덮어쓰기 없음 · 주차·부서를 바꿀 때마다 /taken 으로 다시 판정)
//   ③ «확정» → 넣기로 고른 카드만 보고서로(서버가 한 트랜잭션) · 카드별 오류는 그 카드에 보인다
// 서버 문구가 판정의 정본이다(services/exec_pdf_import.py).

export const MAX_PDF_BYTES = 20 * 1024 * 1024;
const WEEK_KEY_RE = /^\d{4}-W(0[1-9]|[1-4]\d|5[0-3])$/;
const THEMES = [1, 2, 3].map((number) => ({
  number,
  title: `t${number}_title`,
  items: [[`t${number}i1_label`, `t${number}i1`], [`t${number}i2_label`, `t${number}i2`]],
}));
const TEXT_FIELDS = THEMES.flatMap((theme) => [theme.title, ...theme.items.map(([label]) => label)]);
const MAX_KPIS = 100;

const labelStyle = { display: 'block', marginBottom: '6px', color: COLORS.textSub, fontSize: '12px', fontWeight: 700 };

export function errorText(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return typeof detail[0]?.msg === 'string' ? detail[0].msg : fallback;
  if (detail && typeof detail === 'object') {
    if (typeof detail.detail === 'string') return detail.detail;
    if (typeof detail.message === 'string') return detail.message;
  }
  return fallback;
}

function cardErrorNumber(error) {
  const detail = error?.response?.data?.detail;
  if (detail && typeof detail === 'object' && !Array.isArray(detail) && Number.isInteger(detail.card)) return detail.card;
  return null;
}

function toCardState(raw, index) {
  const source = raw || {};
  return {
    index,
    deptRead: source.dept_read || '',
    dept: source.dept || '',
    author: source.author || '',
    reportDate: source.report_date || '',
    pages: source.start_page ? `${source.start_page}~${source.end_page}쪽` : '',
    texts: Object.fromEntries(TEXT_FIELDS.map((field) => [field, source[field] || ''])),
    rich: Object.fromEntries(RICH_FIELDS.map((field) => [field, source[field] || ''])),
    kpis: Array.isArray(source.kpis)
      ? source.kpis.slice(0, MAX_KPIS).map((item) => ({
        label: String(item?.label ?? ''),
        value: String(item?.value ?? ''),
        unit: String(item?.unit ?? ''),
      }))
      : [],
    attachments: Array.isArray(source.attachments) ? source.attachments : [],
    warnings: Array.isArray(source.warnings) ? source.warnings : [],
    readFailed: Boolean(source.read_failed),
    existsAtLoad: Boolean(source.exists),
    include: Boolean(source.dept) && !source.exists && !source.read_failed,
    error: '',
  };
}

function imageCount(rich) {
  return RICH_FIELDS.reduce((total, field) => total + ((rich[field] || '').match(/<img\b/gi) || []).length, 0);
}

function UnusedImage({ uuid, number }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    let objectURL = null;
    // 🔴 보호된 그림이라 맨 <img src="/api/…"> 로는 못 그린다(인증 헤더) — blob 으로 받아 objectURL 로 그린다.
    execMeetingAPI.image(uuid)
      .then((response) => {
        if (!alive) return;
        objectURL = URL.createObjectURL(response.data);
        setUrl(objectURL);
      })
      .catch(() => { if (alive) setFailed(true); });
    return () => {
      alive = false;
      if (objectURL) URL.revokeObjectURL(objectURL);
    };
  }, [uuid]);

  return (
    <li style={{ width: '120px', display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
      <div style={{ width: '120px', height: '90px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.md, overflow: 'hidden', background: COLORS.surface }}>
        {url && <img src={url} alt={`안 쓴 그림 ${number}`} style={{ maxWidth: '100%', maxHeight: '100%' }} />}
        {!url && <span style={{ color: COLORS.textMuted, fontSize: '11px' }}>{failed ? '불러오기 실패' : '불러오는 중'}</span>}
      </div>
      {url && <a href={url} download={`pdf-image-${number}`} aria-label={`안 쓴 그림 ${number} 받기`} style={{ fontSize: '12px' }}>받기</a>}
    </li>
  );
}

function ImportCard({
  cardState, number, departments, exists, busy, expanded, draftId, editorRef,
  onChange, onToggleExpand,
}) {
  const hasDept = Boolean(cardState.dept);
  const canInclude = hasDept && !exists;
  const checked = canInclude && cardState.include;
  const update = (patch) => onChange(cardState.index, patch);
  const updateText = (field, value) => update({ texts: { ...cardState.texts, [field]: value } });
  const updateKpi = (index, field, value) => update({
    kpis: cardState.kpis.map((item, itemIndex) => (itemIndex === index ? { ...item, [field]: value } : item)),
  });
  const deptKnown = !hasDept || departments.some((dept) => dept.name === cardState.dept);

  return (
    <section aria-label={`카드 ${number}`} style={{ border: `1px solid ${exists ? COLORS.borderSoft : COLORS.border}`, borderRadius: RADIUS.lg, padding: '12px', marginBottom: '10px', opacity: exists ? 0.75 : 1 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center', marginBottom: '8px' }}>
        <strong style={{ color: COLORS.text, fontSize: '14px' }}>카드 {number}</strong>
        {cardState.deptRead && <span style={{ color: COLORS.textSub, fontSize: '12px' }}>읽은 부서: {cardState.deptRead}</span>}
        {cardState.pages && <span style={{ color: COLORS.textMuted, fontSize: '12px' }}>{cardState.pages}</span>}
        <span style={{ color: COLORS.textMuted, fontSize: '12px' }}>그림 {imageCount(cardState.rich)}장</span>
        {exists && <span style={badge('warn')}>이미 있음 — 건너뜀</span>}
        {cardState.readFailed && <span style={badge('danger')}>읽기 실패</span>}
      </div>
      {cardState.warnings.length > 0 && (
        <ul aria-label={`카드 ${number} 경고`} style={{ margin: '0 0 8px', paddingLeft: '18px', color: COLORS.warn, fontSize: '12px' }}>
          {cardState.warnings.map((warning, index) => <li key={index}>{warning}</li>)}
        </ul>
      )}
      {cardState.error && <div role="alert" style={{ marginBottom: '8px', color: COLORS.dangerText, fontSize: '13px' }}>{cardState.error}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '8px', alignItems: 'end' }}>
        <label>
          <span style={labelStyle}>부서</span>
          <select aria-label={`카드 ${number} 부서`} value={cardState.dept} disabled={busy} onChange={(event) => update({ dept: event.target.value, include: cardState.dept ? cardState.include : Boolean(event.target.value) })} style={inputStyle}>
            <option value="">부서 고르기</option>
            {!deptKnown && <option value={cardState.dept}>{cardState.dept}</option>}
            {departments.map((dept) => (
              <option key={dept.id ?? dept.name} value={dept.name}>{dept.name}{dept.active === false ? ' (사용 안 함)' : ''}</option>
            ))}
          </select>
        </label>
        <label>
          <span style={labelStyle}>작성자</span>
          <input aria-label={`카드 ${number} 작성자`} value={cardState.author} disabled={busy} onChange={(event) => update({ author: event.target.value })} style={inputStyle} />
        </label>
        <label>
          <span style={labelStyle}>작성일</span>
          <input aria-label={`카드 ${number} 작성일`} type="date" value={cardState.reportDate} disabled={busy} onChange={(event) => update({ reportDate: event.target.value })} style={inputStyle} />
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: COLORS.text }}>
          <input
            type="checkbox"
            aria-label={`카드 ${number} 넣기`}
            checked={checked}
            disabled={busy || !canInclude}
            title={exists ? '그 주차에 같은 부서 보고서가 이미 있습니다' : (!hasDept ? '부서를 먼저 고르세요' : undefined)}
            onChange={(event) => update({ include: event.target.checked })}
          />
          {checked ? '넣기' : '건너뛰기'}
        </label>
      </div>
      {!hasDept && <div style={{ marginTop: '6px', color: COLORS.textMuted, fontSize: '12px' }}>부서를 고르면 넣을 수 있습니다.</div>}

      <div style={{ marginTop: '8px' }}>
        <button type="button" aria-expanded={expanded} aria-label={`카드 ${number} 본문 ${expanded ? '접기' : '고치기'}`} disabled={busy} onClick={() => onToggleExpand(cardState.index)} style={btn('secondary', 'sm')}>
          {expanded ? '본문 접기' : '본문 고치기'}
        </button>
        {!expanded && (
          <span style={{ marginLeft: '8px', color: COLORS.textSub, fontSize: '12px' }}>
            {THEMES.map((theme) => cardState.texts[theme.title]).filter(Boolean).join(' · ') || '제목 없음'}
          </span>
        )}
      </div>

      {expanded && (
        <div style={{ marginTop: '10px' }}>
          {THEMES.map((theme) => (
            <div key={theme.number} style={{ marginBottom: '12px' }}>
              <label>
                <span style={labelStyle}>테마 {theme.number} 제목</span>
                <input aria-label={`카드 ${number} 테마 ${theme.number} 제목`} value={cardState.texts[theme.title]} disabled={busy} onChange={(event) => updateText(theme.title, event.target.value)} style={{ ...inputStyle, marginBottom: '8px' }} />
              </label>
              {theme.items.map(([labelField, richField], index) => (
                <div key={richField} style={{ marginBottom: '8px' }}>
                  <label>
                    <span style={labelStyle}>항목 {index + 1} 라벨</span>
                    <input aria-label={`카드 ${number} 테마 ${theme.number} 항목 ${index + 1} 라벨`} value={cardState.texts[labelField]} disabled={busy} onChange={(event) => updateText(labelField, event.target.value)} style={{ ...inputStyle, marginBottom: '6px' }} />
                  </label>
                  <RichEditor key={`${draftId}-${cardState.index}-${richField}`} ref={editorRef(cardState.index, richField)} field={richField} initialHTML={cardState.rich[richField]} disabled={busy} />
                </div>
              ))}
            </div>
          ))}
          {[['ai_usage', 'AI 활용'], ['etc', '기타']].map(([field, title]) => (
            <div key={field} style={{ marginBottom: '12px' }}>
              <span style={labelStyle}>{title}</span>
              <RichEditor key={`${draftId}-${cardState.index}-${field}`} ref={editorRef(cardState.index, field)} field={field} initialHTML={cardState.rich[field]} disabled={busy} />
            </div>
          ))}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={labelStyle}>KPI</span>
              <button type="button" disabled={busy || cardState.kpis.length >= MAX_KPIS} onClick={() => update({ kpis: [...cardState.kpis, { label: '', value: '', unit: '' }] })} style={btn('secondary', 'sm')}>행 추가</button>
            </div>
            {cardState.kpis.length === 0 && <div style={{ color: COLORS.textMuted, fontSize: '12px' }}>KPI 없음</div>}
            {cardState.kpis.map((kpi, index) => (
              <div key={index} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: '6px', marginTop: '6px' }}>
                <input aria-label={`카드 ${number} KPI ${index + 1} 이름`} value={kpi.label} disabled={busy} onChange={(event) => updateKpi(index, 'label', event.target.value)} style={inputStyle} />
                <input aria-label={`카드 ${number} KPI ${index + 1} 값`} value={kpi.value} disabled={busy} onChange={(event) => updateKpi(index, 'value', event.target.value)} style={inputStyle} />
                <input aria-label={`카드 ${number} KPI ${index + 1} 단위`} value={kpi.unit} disabled={busy} onChange={(event) => updateKpi(index, 'unit', event.target.value)} style={inputStyle} />
                <button type="button" disabled={busy} onClick={() => update({ kpis: cardState.kpis.filter((_, itemIndex) => itemIndex !== index) })} style={btn('danger', 'sm')}>삭제</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

export default function ImportPanel({ pollMs = 2000 }) {
  const [departments, setDepartments] = useState([]);
  const [file, setFile] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);
  const [draftId, setDraftId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [initializedFor, setInitializedFor] = useState(null);
  const [weekKey, setWeekKey] = useState('');
  const [cards, setCards] = useState([]);
  const [expanded, setExpanded] = useState(() => new Set());
  const [taken, setTaken] = useState(null);
  const [outcome, setOutcome] = useState(null);
  const editorRefs = useRef({});
  const takenRequestRef = useRef(0);
  const fileInputRef = useRef(null);

  // 부서 표(사용 안 함 포함 전체) · 새로고침 뒤 이어 보기(읽는 중·미리보기 초안)
  useEffect(() => {
    let alive = true;
    execMeetingAPI.admin.departments()
      .then((response) => { if (alive) setDepartments(Array.isArray(response.data) ? response.data : []); })
      .catch(() => { if (alive) setDepartments([]); });
    execMeetingAPI.admin.importList()
      .then((response) => {
        if (!alive) return;
        const rows = Array.isArray(response.data) ? response.data : [];
        const live = rows.find((row) => row.status === 'reading' || row.status === 'ready');
        if (live) setDraftId((current) => current ?? live.id);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  // 초안 읽기 — reading 이면 pollMs 마다 다시
  useEffect(() => {
    if (!draftId) return undefined;
    let alive = true;
    let timer = null;
    const load = async () => {
      try {
        const response = await execMeetingAPI.admin.importGet(draftId);
        if (!alive) return;
        const data = response.data || {};
        setDraft(data);
        if (data.status === 'reading') timer = setTimeout(load, pollMs);
      } catch (error) {
        if (alive) setNotice({ type: 'error', text: errorText(error, '가져오기 초안을 불러오지 못했습니다.') });
      }
    };
    load();
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [draftId, pollMs]);

  // 미리보기 준비 — 초안마다 한 번
  useEffect(() => {
    if (!draft || draft.status !== 'ready' || initializedFor === draft.id) return;
    const result = draft.result || {};
    setWeekKey(result.week_key || '');
    setCards((result.cards || []).map((raw, index) => toCardState(raw, index)));
    setExpanded(new Set());
    setTaken(null);
    editorRefs.current = {};
    setInitializedFor(draft.id);
  }, [draft, initializedFor]);

  const ready = Boolean(draft && draft.status === 'ready' && initializedFor === draft.id);
  const deptKey = cards.map((cardState) => cardState.dept).join('|');

  // 주차나 카드 부서를 바꿀 때마다 그 주차에 차지된 부서를 다시 받는다(별칭 양쪽 · 삭제된 행 포함 — 서버 판정)
  useEffect(() => {
    if (!ready) return;
    const requestId = takenRequestRef.current + 1;
    takenRequestRef.current = requestId;
    if (!WEEK_KEY_RE.test(weekKey)) {
      setTaken(null);
      return;
    }
    execMeetingAPI.admin.importTaken(draftId, weekKey)
      .then((response) => {
        if (takenRequestRef.current === requestId) setTaken(new Set(Array.isArray(response.data) ? response.data : []));
      })
      .catch(() => {
        if (takenRequestRef.current === requestId) setTaken(null);
      });
  }, [ready, draftId, weekKey, deptKey]);

  const existsOf = useCallback((cardState) => {
    if (!cardState.dept) return false;
    if (taken) return taken.has(cardState.dept);
    return cardState.existsAtLoad;
  }, [taken]);

  const editorRef = useCallback((index, field) => (instance) => {
    const key = `${index}:${field}`;
    if (instance) editorRefs.current[key] = instance;
    else delete editorRefs.current[key];
  }, []);

  const collectRich = async (cardState) => {
    const values = await Promise.all(RICH_FIELDS.map(async (field) => {
      const editor = editorRefs.current[`${cardState.index}:${field}`];
      return [field, editor ? await editor.getHTML() : cardState.rich[field]];
    }));
    return Object.fromEntries(values);
  };

  const changeCard = (index, patch) => {
    setCards((current) => current.map((cardState) => (cardState.index === index ? { ...cardState, ...patch, error: '' } : cardState)));
  };

  const toggleExpand = async (index) => {
    if (expanded.has(index)) {
      const target = cards.find((cardState) => cardState.index === index);
      if (target) {
        const rich = await collectRich(target);
        setCards((current) => current.map((cardState) => (cardState.index === index ? { ...cardState, rich } : cardState)));
      }
      setExpanded((current) => {
        const next = new Set(current);
        next.delete(index);
        return next;
      });
    } else {
      setExpanded((current) => new Set(current).add(index));
    }
  };

  const reset = () => {
    setDraftId(null);
    setDraft(null);
    setInitializedFor(null);
    setCards([]);
    setWeekKey('');
    setTaken(null);
    setExpanded(new Set());
    editorRefs.current = {};
  };

  const chooseFile = (event) => {
    const chosen = event.target.files?.[0] || null;
    setNotice(null);
    setOutcome(null);
    if (!chosen) {
      setFile(null);
      return;
    }
    if (!/\.pdf$/i.test(chosen.name || '')) {
      setFile(null);
      setNotice({ type: 'error', text: 'PDF 파일만 가져올 수 있습니다.' });
      return;
    }
    if (chosen.size > MAX_PDF_BYTES) {
      setFile(null);
      setNotice({ type: 'error', text: 'PDF 는 20MB 까지 올릴 수 있습니다.' });
      return;
    }
    setFile(chosen);
  };

  const startRead = async () => {
    if (!file) return;
    setBusy(true);
    setNotice(null);
    setOutcome(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const response = await execMeetingAPI.admin.importStart(form);
      reset();
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      setDraftId(response.data?.draft_id ?? null);
    } catch (error) {
      setNotice({ type: 'error', text: errorText(error, 'PDF 를 올리지 못했습니다.') });
    } finally {
      setBusy(false);
    }
  };

  const discard = async () => {
    if (!draftId) return;
    const ok = await confirmDialog('이 가져오기 초안을 버릴까요? 읽은 그림도 함께 지워집니다.');
    if (!ok) return;
    setBusy(true);
    setNotice(null);
    try {
      await execMeetingAPI.admin.importCancel(draftId);
      reset();
      setNotice({ type: 'success', text: '가져오기 초안을 버렸습니다.' });
    } catch (error) {
      setNotice({ type: 'error', text: errorText(error, '초안을 버리지 못했습니다.') });
    } finally {
      setBusy(false);
    }
  };

  const includedCount = useMemo(
    () => cards.filter((cardState) => cardState.include && cardState.dept && !existsOf(cardState)).length,
    [cards, existsOf],
  );

  const commit = async () => {
    if (!WEEK_KEY_RE.test(weekKey)) {
      setNotice({ type: 'error', text: '주차는 YYYY-Www 형식(예: 2026-W44)으로 입력해 주세요.' });
      return;
    }
    setBusy(true);
    setNotice(null);
    setCards((current) => current.map((cardState) => ({ ...cardState, error: '' })));
    try {
      const payloadCards = await Promise.all(cards.map(async (cardState) => {
        const rich = expanded.has(cardState.index) ? await collectRich(cardState) : cardState.rich;
        return {
          include: Boolean(cardState.include && cardState.dept && !existsOf(cardState)),
          dept: cardState.dept || null,
          author: cardState.author,
          report_date: cardState.reportDate || null,
          ...cardState.texts,
          ...rich,
          kpis: cardState.kpis.filter((item) => item.label || item.value || item.unit),
        };
      }));
      const response = await execMeetingAPI.admin.importCommit(draftId, { week_key: weekKey, cards: payloadCards });
      setOutcome(response.data || {});
      reset();
    } catch (error) {
      const number = cardErrorNumber(error);
      const text = errorText(error, '보고서를 넣지 못했습니다.');
      if (number !== null) {
        setCards((current) => current.map((cardState) => (cardState.index === number - 1 ? { ...cardState, error: text } : cardState)));
        setNotice({ type: 'error', text: `카드 ${number}: ${text}` });
      } else {
        setNotice({ type: 'error', text });
      }
    } finally {
      setBusy(false);
    }
  };

  const status = draft?.status;
  const result = ready ? (draft.result || {}) : null;
  const unused = Array.isArray(result?.unused_images) ? result.unused_images : [];

  return (
    <div>
      {notice && (
        <div role={notice.type === 'error' ? 'alert' : 'status'} style={{
          marginBottom: '12px', padding: '10px 12px', borderRadius: RADIUS.md, fontSize: '13px',
          color: notice.type === 'error' ? COLORS.dangerText : COLORS.successText,
          background: notice.type === 'error' ? COLORS.dangerSoft : COLORS.successSoft,
        }}>{notice.text}</div>
      )}

      {outcome && (
        <div role="status" aria-label="가져오기 결과" style={{ marginBottom: '12px', padding: '10px 12px', borderRadius: RADIUS.md, fontSize: '13px', color: COLORS.successText, background: COLORS.successSoft }}>
          {`${outcome.inserted ?? 0}건 넣음 · ${outcome.skipped ?? 0}건 건너뜀(이미 있음 ${outcome.existing ?? 0})`}
          {outcome.week_label && <div style={{ marginTop: '4px' }}>통합 탭에서 «{outcome.week_label}» 주차를 열어 확인하세요.</div>}
        </div>
      )}

      {!draftId && (
        <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <label style={{ flex: '1 1 240px', minWidth: 0 }}>
            <span style={labelStyle}>통합 주간 보고서 PDF(20MB · 100쪽까지)</span>
            <input ref={fileInputRef} aria-label="가져올 PDF" type="file" accept=".pdf,application/pdf" disabled={busy} onChange={chooseFile} />
          </label>
          <button type="button" disabled={busy || !file} onClick={startRead} style={btn('primary')}>읽기</button>
        </div>
      )}

      {draftId && !draft && <div role="status" style={{ color: COLORS.textSub, fontSize: '13px' }}>가져오기 초안을 불러오는 중입니다.</div>}

      {draftId && status === 'reading' && (
        <div>
          <div
            role="progressbar"
            aria-label="PDF 읽는 중"
            aria-valuemin={0}
            aria-valuemax={draft.total || 0}
            aria-valuenow={draft.progress || 0}
            style={{ height: '8px', borderRadius: RADIUS.pill, background: COLORS.borderSoft, overflow: 'hidden', marginBottom: '6px' }}
          >
            <div style={{ width: `${draft.total ? Math.min(100, Math.round(((draft.progress || 0) / draft.total) * 100)) : 0}%`, height: '100%', background: COLORS.primary }} />
          </div>
          <div style={{ color: COLORS.textSub, fontSize: '13px' }}>
            {`«${draft.filename || 'PDF'}» 읽는 중… ${draft.progress || 0}/${draft.total || 0}`} — 몇 분 걸릴 수 있습니다.
          </div>
          <button type="button" disabled={busy} onClick={discard} style={{ ...btn('secondary', 'sm'), marginTop: '8px' }}>그만두기</button>
        </div>
      )}

      {draftId && status === 'failed' && (
        <div>
          <div role="alert" style={{ color: COLORS.dangerText, fontSize: '13px', marginBottom: '8px' }}>
            {`«${draft.filename || 'PDF'}» 를 읽지 못했습니다: ${draft.error || '알 수 없는 오류'}`}
          </div>
          <button type="button" disabled={busy} onClick={discard} style={btn('secondary', 'sm')}>닫기</button>
        </div>
      )}

      {ready && (
        <div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '10px' }}>
            <label>
              <span style={labelStyle}>주차(읽은 값: {result.week_label || '없음'})</span>
              <input aria-label="가져올 주차" value={weekKey} disabled={busy} placeholder="2026-W17" onChange={(event) => setWeekKey(event.target.value.trim())} style={{ ...inputStyle, width: '160px' }} />
            </label>
            {!WEEK_KEY_RE.test(weekKey) && <span style={{ color: COLORS.dangerText, fontSize: '12px' }}>YYYY-Www 형식으로 입력하세요.</span>}
            <span style={{ color: COLORS.textSub, fontSize: '12px' }}>{`카드 ${cards.length}개 · 넣을 카드 ${includedCount}개`}</span>
          </div>
          {Array.isArray(result.warnings) && result.warnings.length > 0 && (
            <ul aria-label="그림 읽기 경고" style={{ margin: '0 0 10px', paddingLeft: '18px', color: COLORS.warn, fontSize: '12px' }}>
              {result.warnings.map((warning, index) => <li key={index}>{warning}</li>)}
            </ul>
          )}
          {cards.length === 0 && <div style={{ color: COLORS.textMuted, fontSize: '13px', marginBottom: '10px' }}>읽은 부서 카드가 없습니다.</div>}
          {cards.map((cardState) => (
            <ImportCard
              key={`${draft.id}-${cardState.index}`}
              cardState={cardState}
              number={cardState.index + 1}
              departments={departments}
              exists={existsOf(cardState)}
              busy={busy}
              expanded={expanded.has(cardState.index)}
              draftId={draft.id}
              editorRef={editorRef}
              onChange={changeCard}
              onToggleExpand={toggleExpand}
            />
          ))}

          {unused.length > 0 && (
            <section aria-label="안 쓴 그림" style={{ ...card, marginTop: '10px' }}>
              <div style={{ color: COLORS.textSub, fontSize: '12px', marginBottom: '8px' }}>
                어느 카드 본문에도 안 들어간 그림입니다. 확정·버리기 때 지워집니다 — 필요하면 받은 뒤 편집기에 붙여넣으세요.
              </div>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                {unused.map((uuid, index) => <UnusedImage key={uuid} uuid={uuid} number={index + 1} />)}
              </ul>
            </section>
          )}

          <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
            <button type="button" disabled={busy || !WEEK_KEY_RE.test(weekKey)} onClick={commit} style={btn('primary')}>확정</button>
            <button type="button" disabled={busy} onClick={discard} style={btn('secondary')}>초안 버리기</button>
          </div>
        </div>
      )}
    </div>
  );
}
